import { useState } from 'react';
import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/Spinner/spinner';
import { contractData } from '../../../types/interfaces';
import { downloadContractFile } from '../../../services/InstrumentationService';
import { getMimeType } from '../../../utils/Utils';
import styled from 'styled-components';

const ContractsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContractCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
  box-sizing: border-box;
  min-width: 0;
`;

const ContractIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ContractLabelContainer = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ContractActionContainer = styled.div<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
`;

interface ContractsReadOnlyProps {
  contracts: contractData[];
  isLoading: boolean;
  cuit: string | undefined;
  applicationId: string | undefined;
}

const ContractsReadOnly: React.FC<ContractsReadOnlyProps> = ({
  contracts,
  isLoading,
  cuit,
  applicationId,
}) => {
  const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({});

  const handleViewContract = async (contract: contractData) => {
    if (!contract.asset_file_path || !cuit || !applicationId) return;

    const loadingKey = contract.id || contract.asset_file_path;
    setIsDownloading((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      const response = await downloadContractFile(
        cuit,
        applicationId,
        contract.asset_file_path
      );

      if (response.success) {
        const filename = contract.asset_file_path.split('/').pop() || contract.asset_file_path;
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);

        if (
          mimeType.startsWith('application/pdf') ||
          mimeType.startsWith('image/')
        ) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading contract:', error);
    } finally {
      setIsDownloading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  return (
    <Card margin={{ top: '4' }}>
      <Card variant='success' modifier='weak' spacing={'2'}>
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Contratos
        </H>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }} display='flex' justifyContent='center'>
          <Spinner size='md' />
        </Card>
      ) : contracts.length > 0 ? (
        <Card margin={{ top: '4' }} width='100%'>
          <ContractsGridContainer>
            {contracts.map((contract, index) => {
              const loadingKey = contract.id || contract.asset_file_path;
              const isDisabled = !contract.asset_file_path;
              return (
                <ContractCard key={index}>
                  <ContractIconContainer>
                    <Icon variant='neutral' name='document_outline' height={24} width={24} />
                  </ContractIconContainer>
                  <ContractLabelContainer>
                    <P
                      type='3'
                      withMargin={false}
                      variant='neutral'
                      modifier='strong'
                    >
                      {contract.template_name || 'Contrato'}
                    </P>
                  </ContractLabelContainer>
                  <ContractActionContainer
                    disabled={isDisabled}
                    onClick={() => !isDisabled && handleViewContract(contract)}
                  >
                    {isDownloading[loadingKey] ? (
                      <Spinner size='sm' />
                    ) : (
                      <Icon
                        name='view_outline'
                        variant='neutral'
                        height={24}
                        width={24}
                        cursor={isDisabled ? 'not-allowed' : 'pointer'}
                      />
                    )}
                  </ContractActionContainer>
                </ContractCard>
              );
            })}
          </ContractsGridContainer>
        </Card>
      ) : (
        <Card margin={{ top: '4' }}>
          <P type='2' variant='neutral' modifier='weak' withMargin={false}>
            No hay contratos firmados
          </P>
        </Card>
      )}
    </Card>
  );
};

export default ContractsReadOnly;

